import clsx from 'clsx';

/**
 * Colored status badge shown next to a page title (sidebar + doc view).
 * Known statuses get semantic colors; anything else renders as a neutral
 * gray badge with the literal text. Renders nothing when status is empty.
 *
 * Set via the publish API (`status` field / publish.sh 5th arg) or the
 * status input in the doc editor.
 */

const COLOR: Record<string, string> = {
  // yellow/amber — in beta
  beta: 'border-amber-400/60 bg-amber-100 text-amber-800 dark:border-amber-600/60 dark:bg-amber-950/50 dark:text-amber-300',
  // green — shipped
  live: 'border-green-500/50 bg-green-100 text-green-800 dark:border-green-600/60 dark:bg-green-950/50 dark:text-green-300',
  shipped:
    'border-green-500/50 bg-green-100 text-green-800 dark:border-green-600/60 dark:bg-green-950/50 dark:text-green-300',
  // blue — under review
  'in-review':
    'border-blue-400/60 bg-blue-100 text-blue-800 dark:border-blue-600/60 dark:bg-blue-950/50 dark:text-blue-300',
  review:
    'border-blue-400/60 bg-blue-100 text-blue-800 dark:border-blue-600/60 dark:bg-blue-950/50 dark:text-blue-300',
  // gray — not started / draft
  draft: 'border-border bg-surface-2 text-ink-muted',
  planned: 'border-border bg-surface-2 text-ink-muted',
};

const NEUTRAL = 'border-border bg-surface-2 text-ink-muted';

export default function StatusBadge({
  status,
  size = 'sm',
}: {
  status?: string | null;
  size?: 'sm' | 'md';
}) {
  const key = status?.trim().toLowerCase();
  if (!key) return null;
  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border font-semibold uppercase tracking-wide',
        size === 'md' ? 'px-2.5 py-0.5 text-xs' : 'px-1.5 py-px text-[10px]',
        COLOR[key] ?? NEUTRAL
      )}
      title={`Status: ${key}`}
    >
      {key}
    </span>
  );
}
